import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';

import Button from '../../components/ui/Button';
import { submitTrainingEvaluation } from '../../services/trainingService';

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; }`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.15s ease;
`;

const Dialog = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  max-width: 520px;
  width: 95%;
  animation: ${slideUp} 0.2s ease;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const Title = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: #64748B;
  margin: 0 0 16px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 14px 0 6px;
`;

const StarRow = styled.div`
  display: flex;
  gap: 6px;
`;

const StarButton = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${({ $active }) => ($active ? '#F59E0B' : '#CBD5E1')};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 70px;
  padding: 10px;
  border: 1px solid #CBD5E1;
  border-radius: 6px;
  resize: vertical;
  font-family: inherit;
  font-size: 13px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary.main}; }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

export default function EvaluationModal({ assignmentId, onClose, onSubmitted }) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [contentComment, setContentComment] = useState('');
  const [instructorComment, setInstructorComment] = useState('');
  const [loading, setLoading] = useState(false);

  const save = async () => {
    if (rating < 1) return;
    setLoading(true);
    try {
      await submitTrainingEvaluation(assignmentId, {
        rating,
        contentComment: contentComment.trim() || null,
        instructorComment: instructorComment.trim() || null,
      });
      onSubmitted?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Title>{t('training.evaluationTitle')}</Title>
        <Description>{t('training.evaluationIntro')}</Description>

        <FieldLabel>{t('training.evaluationRating')} *</FieldLabel>
        <StarRow>
          {[1, 2, 3, 4, 5].map((n) => (
            <StarButton
              key={n}
              type="button"
              $active={n <= rating}
              onClick={() => setRating(n)}
              aria-label={`${n} star`}
            >
              <Star size={28} fill={n <= rating ? '#F59E0B' : 'none'} />
            </StarButton>
          ))}
        </StarRow>

        <FieldLabel>{t('training.evaluationContentComment')}</FieldLabel>
        <TextArea value={contentComment} onChange={(e) => setContentComment(e.target.value)} />

        <FieldLabel>{t('training.evaluationInstructorComment')}</FieldLabel>
        <TextArea value={instructorComment} onChange={(e) => setInstructorComment(e.target.value)} />

        <Actions>
          <Button type="button" $variant="outline" onClick={onClose} disabled={loading}>
            {t('common.skip')}
          </Button>
          <Button type="button" $variant="primary" onClick={save} disabled={loading || rating < 1}>
            {t('common.save')}
          </Button>
        </Actions>
      </Dialog>
    </Overlay>
  );
}
